import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { updateQADatabaseFromResponses, regenerateResponseWithContext } from '../services/api';
import { Project, ProcessedRow, CustomField, SubscriptionTier } from '../types';
import MainLayout from '../components/MainLayout';
import CSVUpload from '../components/CSVUpload';
import ProcessingStatus from '../components/ProcessingStatus';
import ResultsTable, { RegeneratedResponse } from '../components/ResultsTable';
import KnowledgeBase from '../components/KnowledgeBase';
import ProjectSettings from '../components/ProjectSettings';
import InviteManagement from '../components/InviteManagement';
import SubscriptionManagement from '../components/SubscriptionManagement';
import { SlackIntegration } from '../components/integrations';
import './DashboardPage.css';

const DashboardPage: React.FC = () => {
  const { user, userContext, signOut, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sidebar section state
  const [activeSection, setActiveSection] = useState('questionnaire');

  // Processing state
  const [jobId, setJobId] = useState<string | null>(null);
  const [processedRows, setProcessedRows] = useState<ProcessedRow[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [view, setView] = useState<'upload' | 'processing' | 'results'>('upload');
  const [isUpdatingQA, setIsUpdatingQA] = useState(false);
  const [qaUpdateSuccess, setQaUpdateSuccess] = useState<string | null>(null);

  // Get the project ID from auth context or user object
  // For backend API calls (chat, knowledge base), use domain-based ID
  const projectId = user?.project || userContext?.project?.domain;
  // For Supabase settings, use project_slug (e.g., 'factors' instead of 'factors.ai')
  const projectSlug = userContext?.project?.project_slug || projectId;
  const userRole = user?.role || userContext?.membership?.role;
  const displayName = user?.fullName || userContext?.profile?.full_name || user?.email;
  const subscriptionTier = (userContext?.project?.subscription_tier || 'free') as SubscriptionTier;

  useEffect(() => {
    if (authLoading) return;

    if (!user || !projectId) {
      navigate('/login');
      return;
    }

    // Use Supabase data directly (fast) - no need to call slow backend
    if (userContext?.project) {
      setProject({
        project_id: userContext.project.project_slug || userContext.project.domain,
        company_name: userContext.project.company_name,
        description: userContext.project.description || null,
        verbosity: userContext.project.response_verbosity || 'nominal',
        // These flags require S3 check - assume true for now,
        // Knowledge Base page will show actual status
        has_policies: true,
        has_qa_database: true,
        has_urls: true,
      });
      setLoading(false);
    } else {
      setError('Failed to load project details');
      setLoading(false);
    }
  }, [user, projectId, userContext, authLoading, navigate]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleUploadStart = (newJobId: string, fields: CustomField[]) => {
    setJobId(newJobId);
    setCustomFields(fields);
    setView('processing');
  };

  const handleProcessingComplete = (rows: ProcessedRow[]) => {
    setProcessedRows(rows);
    setView('results');
  };

  const handleReset = () => {
    setJobId(null);
    setProcessedRows([]);
    setView('upload');
  };

  const handleUpdateRow = (id: string, field: string, value: string) => {
    setProcessedRows(rows =>
      rows.map(row =>
        row.id === id ? { ...row, [field]: value, isEdited: true } : row
      )
    );
  };

  const handleUpdateRowSources = (id: string, sources: { doc_name: string; url?: string }[]) => {
    // Convert undefined urls to null to match BatchSourceInfo type
    const normalizedSources = sources.map(s => ({
      doc_name: s.doc_name,
      url: s.url ?? null
    }));
    setProcessedRows(rows =>
      rows.map(row =>
        row.id === id ? { ...row, sources: normalizedSources, isEdited: true } : row
      )
    );
  };

  const handleUpdateQADatabase = async (editedRows: ProcessedRow[], customerName: string) => {
    if (!projectId) return;

    setIsUpdatingQA(true);
    setQaUpdateSuccess(null);
    setError(null);

    try {
      // Convert edited rows to Q&A entries
      const entries = editedRows.map(row => ({
        question: row.query,
        answer: row.response
      }));

      // Pass the user-provided customer name (the client who sent the questionnaire)
      const result = await updateQADatabaseFromResponses(
        projectId,
        entries,
        customerName
      );

      // Mark updated rows as no longer edited (they've been saved)
      setProcessedRows(rows =>
        rows.map(row =>
          editedRows.find(er => er.id === row.id) ? { ...row, isEdited: false } : row
        )
      );

      setQaUpdateSuccess(result.message);
      setTimeout(() => setQaUpdateSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update Q&A database');
    } finally {
      setIsUpdatingQA(false);
    }
  };

  // Handler for adding a single response to Q&A database
  const handleAddSingleToQA = async (row: ProcessedRow, customerName: string) => {
    if (!projectId) return;

    setError(null);

    try {
      const entries = [{
        question: row.query,
        answer: row.response
      }];

      const result = await updateQADatabaseFromResponses(
        projectId,
        entries,
        customerName
      );

      setQaUpdateSuccess(`Added 1 Q&A pair to database`);
      setTimeout(() => setQaUpdateSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add to Q&A database');
      throw err; // Re-throw so the component knows it failed
    }
  };

  // Handler for regenerating a response with additional context
  const handleRegenerateResponse = async (row: ProcessedRow, additionalContext: string): Promise<RegeneratedResponse> => {
    if (!projectId) throw new Error('No project selected');

    setError(null);

    try {
      const result = await regenerateResponseWithContext(
        row.query,
        additionalContext,
        projectId,
        true // include debug info
      );

      setQaUpdateSuccess('Response regenerated successfully');
      setTimeout(() => setQaUpdateSuccess(null), 3000);

      return {
        response: result.response,
        confidence: result.confidence,
        sources: result.sources.map(s => {
          if (typeof s === 'string') {
            return { doc_name: s };
          }
          // Handle DocSource (has doc_name) vs QASource (has type: 'previous_qa')
          if ('doc_name' in s) {
            return { doc_name: s.doc_name, url: s.url };
          }
          // QASource - use client as the display name
          if ('client' in s) {
            return { doc_name: `Q&A: ${s.client}` };
          }
          return { doc_name: 'Unknown' };
        })
      };
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to regenerate response');
      throw err;
    }
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'questionnaire':
        return (
          <div className="questionnaire-section">
            {error && <div className="error-banner">{error}</div>}
            {qaUpdateSuccess && <div className="success-banner">{qaUpdateSuccess}</div>}

            {project && (
              <div className="project-info-card">
                <h2>Project: {project.company_name}</h2>
                <div className="project-stats">
                  <div className="stat">
                    <span className="stat-icon">📄</span>
                    <span>Policies: {project.has_policies ? '✅' : '❌'}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-icon">💾</span>
                    <span>Q&A Database: {project.has_qa_database ? '✅' : '❌'}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-icon">🔗</span>
                    <span>URLs: {project.has_urls ? '✅' : '❌'}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="content-card">
              {view === 'upload' && projectId && (
                <CSVUpload projectId={projectId} onUploadStart={handleUploadStart} />
              )}

              {view === 'processing' && jobId && (
                <ProcessingStatus
                  jobId={jobId}
                  onComplete={handleProcessingComplete}
                  onError={(err) => setError(err)}
                />
              )}

              {view === 'results' && projectId && (
                <ResultsTable
                  rows={processedRows}
                  customFields={customFields}
                  projectId={projectId}
                  onUpdateRow={handleUpdateRow}
                  onUpdateRowSources={handleUpdateRowSources}
                  onReset={handleReset}
                  onUpdateQADatabase={handleUpdateQADatabase}
                  onAddSingleToQA={handleAddSingleToQA}
                  onRegenerateResponse={handleRegenerateResponse}
                  isUpdatingQA={isUpdatingQA}
                />
              )}
            </div>
          </div>
        );

      case 'knowledge-base':
        return projectId ? <KnowledgeBase projectId={projectId} /> : null;

      case 'team':
        return <InviteManagement />;

      case 'subscription':
        return <SubscriptionManagement currentTier={subscriptionTier} />;

      case 'integrations':
        return (
          <div className="integrations-section">
            <div className="section-header">
              <h2>Integrations</h2>
              <p>Connect external services to enhance your workflow</p>
            </div>
            <SlackIntegration />
          </div>
        );

      case 'settings':
        // Use projectSlug for Supabase-based settings
        // Pass isAdmin to enable BYOK settings for admins
        return projectSlug ? <ProjectSettings projectId={projectSlug} isAdmin={userRole === 'admin'} /> : null;

      default:
        return null;
    }
  };

  return (
    <MainLayout
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      projectName={project?.company_name || userContext?.project?.company_name}
      projectId={projectId}
      username={displayName}
      userRole={userRole}
      subscriptionTier={subscriptionTier}
      onLogout={handleLogout}
    >
      {renderContent()}
    </MainLayout>
  );
};

export default DashboardPage;

